/**
 * VideoEditorBottomPanel -- Tabbed floating panel for Video Editor node.
 *
 * Contains 2 tabs:
 * - References: Horizontal thumbnail row of connected video/audio clips
 *   with an "Add References" button to open ReferenceSelector.
 * - Timeline Editor: Single "Open Timeline Editor" button.
 *
 * Follows floating panel pattern: nopan nodrag nowheel, inverse-zoom scaling,
 * spring animation (stiffness 400, damping 25).
 */

import { memo, useState } from 'react'
import { useViewport } from 'reactflow'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '../../../../lib/utils'

export type VideoEditorTab = 'references' | 'timeline'

export interface ClipReference {
  id: string
  path: string
  name: string
}

interface VideoEditorBottomPanelProps {
  isVisible: boolean
  videoClips: ClipReference[]
  audioClips: ClipReference[]
  onOpenReferenceSelector: () => void
  onOpenEditor?: () => void
}

const TABS: { id: VideoEditorTab; label: string }[] = [
  { id: 'references', label: 'References' },
  { id: 'timeline', label: 'Timeline Editor' },
]

/* Inline SVG paths for clip type icons */
const VIDEO_ICON_PATH =
  'M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z'
const AUDIO_ICON_PATH =
  'M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2z'

/** Shared clip thumbnail card used for both video and audio rows. */
function ClipCard({ clip, iconPath, thumbnailStyle }: {
  clip: ClipReference
  iconPath: string
  thumbnailStyle?: React.CSSProperties
}) {
  return (
    <div className="flex-shrink-0 w-24 rounded-lg border border-border-subtle bg-surface-2 overflow-hidden">
      <div
        className="w-full bg-black/30 flex items-center justify-center"
        style={thumbnailStyle}
      >
        <svg
          className="w-5 h-5 text-text-muted/40"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d={iconPath}
          />
        </svg>
      </div>
      <div className="px-1.5 py-1">
        <span className="text-[10px] text-text-secondary truncate block">
          {clip.name}
        </span>
      </div>
    </div>
  )
}

export const VideoEditorBottomPanel = memo(function VideoEditorBottomPanel({
  isVisible,
  videoClips,
  audioClips,
  onOpenReferenceSelector,
  onOpenEditor,
}: VideoEditorBottomPanelProps) {
  const { zoom } = useViewport()
  const scale = Math.min(Math.max(1 / zoom, 0.5), 2)
  const [activeTab, setActiveTab] = useState<VideoEditorTab>('references')

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: -10, scale, x: '-50%' }}
          animate={{ opacity: 1, y: 0, scale, x: '-50%' }}
          exit={{ opacity: 0, y: -10, scale, x: '-50%' }}
          transition={{
            type: 'spring',
            stiffness: 400,
            damping: 25,
            opacity: { duration: 0.15 },
          }}
          className="absolute z-50 nopan nodrag nowheel"
          style={{
            top: '100%',
            left: '50%',
            marginTop: 8,
            transformOrigin: 'top center',
            width: 'calc(100% + 120px)',
          }}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-surface-1 border border-border rounded-xl shadow-2xl overflow-hidden">
            {/* Tab Bar */}
            <div className="flex gap-1 p-2 border-b border-border-subtle">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    'flex-1 px-3 py-1.5 text-xs font-medium rounded-full relative transition-all duration-200',
                    activeTab === tab.id
                      ? ''
                      : 'text-text-muted hover:text-foreground'
                  )}
                  style={
                    activeTab === tab.id
                      ? {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          color: '#ffffff',
                        }
                      : undefined
                  }
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab Content -- 220px fixed height */}
            <div className="h-[220px]">
              {/* References Tab */}
              {activeTab === 'references' && (
                <div className="h-full p-3 overflow-y-auto nowheel">
                  {videoClips.length === 0 && audioClips.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center">
                      <svg
                        className="w-8 h-8 text-text-muted/30 mb-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1}
                          d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                        />
                      </svg>
                      <p className="text-xs text-text-muted">
                        No clips connected
                      </p>
                      <p className="text-xxs text-text-muted/60 mt-1">
                        Add video and audio references to build your timeline
                      </p>
                      <button
                        onClick={onOpenReferenceSelector}
                        className="mt-3 px-4 py-1.5 text-xs rounded-full bg-primary/90 hover:bg-primary text-primary-foreground font-medium transition-colors"
                      >
                        Add References
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-3 h-full">
                      {/* Video clips row */}
                      {videoClips.length > 0 && (
                        <div>
                          <h4 className="text-[10px] text-text-muted mb-1.5 font-medium uppercase tracking-wide">
                            Video Clips
                          </h4>
                          <div className="flex gap-2 overflow-x-auto pb-1 nowheel">
                            {videoClips.map((clip) => (
                              <ClipCard
                                key={clip.id}
                                clip={clip}
                                iconPath={VIDEO_ICON_PATH}
                                thumbnailStyle={{ aspectRatio: '16/9' }}
                              />
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Audio clips row */}
                      {audioClips.length > 0 && (
                        <div>
                          <h4 className="text-[10px] text-text-muted mb-1.5 font-medium uppercase tracking-wide">
                            Audio Clips
                          </h4>
                          <div className="flex gap-2 overflow-x-auto pb-1 nowheel">
                            {audioClips.map((clip) => (
                              <ClipCard
                                key={clip.id}
                                clip={clip}
                                iconPath={AUDIO_ICON_PATH}
                                thumbnailStyle={{ height: 40 }}
                              />
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Add References button */}
                      <div className="mt-auto flex justify-center pb-1">
                        <button
                          onClick={onOpenReferenceSelector}
                          className="px-4 py-1.5 text-xs rounded-full border border-border-subtle hover:border-border text-text-secondary hover:text-foreground transition-all"
                        >
                          Add References
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Timeline Editor Tab */}
              {activeTab === 'timeline' && (
                <div className="h-full flex items-center justify-center">
                  <button
                    onClick={onOpenEditor}
                    className="px-6 py-2.5 text-sm rounded-lg bg-primary/90 hover:bg-primary text-primary-foreground font-medium transition-colors"
                  >
                    Open Timeline Editor
                  </button>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
})

VideoEditorBottomPanel.displayName = 'VideoEditorBottomPanel'
